import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Single-select dropdown with type-to-filter search.
 * options: [{ value: string|number, label: string }]
 */
export default function SingleSelectSearch({
    options = [],
    value = '',
    onChange,
    placeholder,
    emptyLabel = null,
    loading = false,
    loadError = false,
    disabled = false,
    className = '',
}) {
    const { t } = useTranslation();
    const resolvedPlaceholder = placeholder ?? t('selectSearch.select');
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);
    const inputRef = useRef(null);

    const valueStr = value === null || value === undefined ? '' : String(value);

    const selectedLabel = useMemo(() => {
        if (valueStr === '' && emptyLabel != null) return emptyLabel;
        const match = options.find((opt) => String(opt.value) === valueStr);
        return match?.label || resolvedPlaceholder;
    }, [options, valueStr, emptyLabel, resolvedPlaceholder]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return options;
        return options.filter((opt) => (opt.label || '').toLowerCase().includes(q));
    }, [options, query]);

    useEffect(() => {
        if (!open) return undefined;
        const onDocClick = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) {
                setOpen(false);
                setQuery('');
            }
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [open]);

    useEffect(() => {
        if (open) {
            setQuery('');
            requestAnimationFrame(() => inputRef.current?.focus());
        }
    }, [open]);

    const pick = (next) => {
        onChange?.(next);
        setOpen(false);
        setQuery('');
    };

    return (
        <div ref={rootRef} className={`relative ${className}`}>
            <button
                type="button"
                disabled={disabled}
                onClick={() => !disabled && setOpen((v) => !v)}
                className={`w-full border border-gray-300 rounded-xl px-3 py-2 text-left bg-white
                    focus:ring-2 focus:ring-primary-500 focus:border-transparent
                    disabled:bg-gray-100 disabled:cursor-not-allowed
                    flex items-center justify-between gap-2 min-h-[42px]`}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                <span className={`truncate text-sm ${valueStr === '' && !emptyLabel ? 'text-gray-400' : 'text-gray-800'}`}>
                    {selectedLabel}
                </span>
                <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {open && (
                <div className="absolute z-30 w-full mt-1 bg-white border border-gray-300 rounded-xl shadow-lg overflow-hidden">
                    <div className="p-2 border-b border-gray-200 bg-white sticky top-0">
                        <input
                            ref={inputRef}
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder={t('selectSearch.search')}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => {
                                if (e.key === 'Escape') {
                                    setOpen(false);
                                    setQuery('');
                                }
                            }}
                        />
                    </div>
                    <ul role="listbox" className="max-h-56 overflow-y-auto">
                        {emptyLabel != null && (
                            <li>
                                <button
                                    type="button"
                                    className={`w-full text-left px-3 py-2 text-sm hover:bg-primary-50 ${
                                        valueStr === '' ? 'bg-primary-50 font-medium text-primary-800' : 'text-gray-600'
                                    }`}
                                    onClick={() => pick('')}
                                >
                                    {emptyLabel}
                                </button>
                            </li>
                        )}
                        {loading ? (
                            <li className="px-3 py-3 text-sm text-gray-500 text-center">{t('selectSearch.loading')}</li>
                        ) : loadError ? (
                            <li className="px-3 py-3 text-sm text-rose-600 text-center">{t('selectSearch.loadFailed')}</li>
                        ) : filtered.length === 0 ? (
                            <li className="px-3 py-3 text-sm text-gray-500 text-center">{t('selectSearch.noOptions')}</li>
                        ) : (
                            filtered.map((opt) => {
                                const optVal = String(opt.value);
                                const active = optVal === valueStr;
                                return (
                                    <li key={optVal}>
                                        <button
                                            type="button"
                                            role="option"
                                            aria-selected={active}
                                            className={`w-full text-left px-3 py-2 text-sm hover:bg-primary-50 ${
                                                active ? 'bg-primary-50 font-medium text-primary-800' : 'text-gray-800'
                                            }`}
                                            onClick={() => pick(optVal)}
                                        >
                                            {opt.label}
                                        </button>
                                    </li>
                                );
                            })
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
}
